<template>
  <transition name="overlay-fade">
    <div
      v-if="visible"
      class="context-switch-overlay"
      style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 10000; display: flex; align-items: center; justify-content: center; background-color: rgba(255, 255, 255, 0.95); backdrop-filter: blur(4px);"
    >
      <div style="display: flex; flex-direction: column; align-items: center; gap: 32px;">
        <!-- Logo with multiple spinning rings -->
        <div style="position: relative; width: 160px; height: 160px;">
          <div style="width: 160px; height: 160px; border-radius: 50%; overflow: hidden; padding: 24px;">
            <img
              src="@/assets/img/logo.png"
              alt="Oelsnitz-Ticket Logo"
              style="width: 100%; height: 100%; object-fit: contain;"
              @error="handleLogoError"
            />
          </div>
          <!-- Multiple spinning rings -->
          <div
            class="spinning-ring ring-1"
            style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; border-radius: 50%; border: 4px solid #5e72e4; border-top-color: transparent;"
          ></div>
          <div
            class="spinning-ring ring-2"
            style="position: absolute; top: -8px; left: -8px; right: -8px; bottom: -8px; border-radius: 50%; border: 4px solid rgba(94, 114, 228, 0.5); border-right-color: transparent;"
          ></div>
          <div
            class="spinning-ring ring-3"
            style="position: absolute; top: -16px; left: -16px; right: -16px; bottom: -16px; border-radius: 50%; border: 4px solid rgba(94, 114, 228, 0.3); border-bottom-color: transparent;"
          ></div>
        </div>
        
        <!-- Loading text with transitions -->
        <div style="display: flex; flex-direction: column; align-items: center; min-height: 120px; width: 100%; gap: 16px;">
          <transition name="phase-fade" mode="out-in">
            <div :key="phase" style="display: flex; flex-direction: column; align-items: center;">
              <p class="text-h5 font-weight-bold mb-2" style="color: #344767;">
                {{ phaseText }}
              </p>
              <p class="text-sm" style="color: #67748e;">
                {{ phaseSubtext }}
              </p>
            </div>
          </transition>
          
          <!-- Progress bar -->
          <div style="display: flex; flex-direction: column; align-items: center; margin-top: 16px; width: 256px; gap: 8px;">
            <div style="width: 100%; height: 8px; background-color: #e9ecef; border-radius: 4px; overflow: hidden; position: relative;">
              <div
                style="height: 100%; background-color: #5e72e4; border-radius: 4px;"
                :style="{ width: progress + '%' }"
              ></div>
            </div>
            <p class="text-xs" style="color: #67748e;">
              {{ Math.round(progress) }}%
            </p>
          </div>
        </div>
        
        <!-- Bouncing dots -->
        <div style="display: flex; gap: 8px;">
          <span
            class="bouncing-dot dot-1"
            style="width: 12px; height: 12px; border-radius: 50%; background-color: #5e72e4;"
          ></span>
          <span
            class="bouncing-dot dot-2"
            style="width: 12px; height: 12px; border-radius: 50%; background-color: #5e72e4;"
          ></span>
          <span
            class="bouncing-dot dot-3"
            style="width: 12px; height: 12px; border-radius: 50%; background-color: #5e72e4;"
          ></span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ContextSwitchOverlay',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      phase: 0,
      progress: 0,
      intervals: [],
      phases: [
        { text: 'Organisation wechseln...', subtext: 'Daten werden geladen' },
        { text: 'Berechtigungen prüfen...', subtext: 'Zugriffsrechte werden aktualisiert' },
        { text: 'Fast fertig...', subtext: 'Letzte Anpassungen' }
      ]
    }
  },
  computed: {
    phaseText() {
      return this.phases[this.phase]?.text || 'Laden...'
    },
    phaseSubtext() {
      return this.phases[this.phase]?.subtext || 'Bitte warten'
    }
  },
  watch: {
    visible(newVal, oldVal) {
      console.log('👁️ [OVERLAY] visible changed:', oldVal, '->', newVal)
      if (newVal) {
        console.log('▶️ [OVERLAY] Starting progress animation')
        this.startProgress()
      } else {
        console.log('⏹️ [OVERLAY] Resetting progress')
        this.resetProgress()
      }
    }
  },
  mounted() {
    // Start progress if visible is already true (e.g., if component is mounted while switching)
    if (this.visible) {
      this.startProgress()
    } else {
      this.resetProgress()
    }
  },
  methods: {
    handleLogoError(event) {
      event.target.style.display = 'none'
    },
    startProgress() {
      // Clear any existing intervals first
      this.resetProgress()
      
      this.phase = 0
      this.progress = 0
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Total duration: ~2 seconds (800ms + 800ms + 400ms)
        // Phase 1: 0-40% (~0.8 seconds, 40 steps * 20ms)
        const phase1Interval = setInterval(() => {
          if (this.progress < 40) {
            this.progress = Math.min(this.progress + 1, 40)
          } else {
            clearInterval(phase1Interval)
            this.phase = 1
            // Phase 2: 40-80% (~0.8 seconds, 40 steps * 20ms)
            const phase2Interval = setInterval(() => {
              if (this.progress < 80) {
                this.progress = Math.min(this.progress + 1, 80)
              } else {
                clearInterval(phase2Interval)
                this.phase = 2
                // Phase 3: 80-100% (~0.4 seconds, 20 steps * 20ms)
                const phase3Interval = setInterval(() => {
                  if (this.progress < 100) {
                    this.progress = Math.min(this.progress + 1, 100)
                  } else {
                    clearInterval(phase3Interval)
                  }
                }, 20)
                this.intervals.push(phase3Interval)
              }
            }, 20)
            this.intervals.push(phase2Interval)
          }
        }, 20)
        this.intervals.push(phase1Interval)
      }, 50)
    },
    resetProgress() {
      // Clear all intervals
      this.intervals.forEach(interval => {
        clearInterval(interval)
      })
      this.intervals = []
      this.phase = 0
      this.progress = 0
    }
  },
  beforeDestroy() {
    this.resetProgress()
  }
}
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes spin-reverse {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.spinning-ring.ring-1 {
  animation: spin 1s linear infinite;
}

.spinning-ring.ring-2 {
  animation: spin-reverse 1.5s linear infinite;
}

.spinning-ring.ring-3 {
  animation: spin 2s linear infinite;
}

.bouncing-dot.dot-1 {
  animation: bounce 1.4s ease-in-out infinite;
  animation-delay: 0s;
}

.bouncing-dot.dot-2 {
  animation: bounce 1.4s ease-in-out infinite;
  animation-delay: 0.2s;
}

.bouncing-dot.dot-3 {
  animation: bounce 1.4s ease-in-out infinite;
  animation-delay: 0.4s;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-fade-enter,
.overlay-fade-leave-to {
  opacity: 0;
}

.phase-fade-enter-active,
.phase-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.phase-fade-enter,
.phase-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
